import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, ModalBody, ModalHeader, Alert } from 'reactstrap';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import {
  Lock as PrivateIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import { updateIntegrationVisibility } from '../../store/Integration';
import './IntegrationToggle.css';
import { border, width } from '../../../node_modules/@mui/system/index';

/**
 * IntegrationToggle Component
 *
 * Allows users to toggle integration visibility between Private and Public
 *
 * @param {string} integrationId - Integration ID
 * @param {string} visibilityType - Current visibility ('private' or 'public')
 * @param {string} integrationName - Name of the integration (e.g., "Gmail", "Twilio SMS")
 * @param {boolean} disabled - Whether the toggle is disabled
 * @param {function} onUpdate - Optional callback when visibility is updated
 */
const IntegrationToggle = ({
  integrationId,
  visibilityType = 'private',
  integrationName = 'Integration',
  disabled = false,
  onUpdate
}) => {
  const dispatch = useDispatch();
  const [localValue, setLocalValue] = useState(visibilityType);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingValue, setPendingValue] = useState(null);

  const handleChange = (event) => {
    const newValue = event.target.value;

    // If changing to public, show confirmation dialog
    if (newValue === 'public') {
      setPendingValue(newValue);
      setShowConfirmDialog(true);
    } else {
      // Changing to private, no confirmation needed
      updateVisibility(newValue);
    }
  };

  const handleConfirmPublic = () => {
    updateVisibility(pendingValue);
    setShowConfirmDialog(false);
    setPendingValue(null);
  };

  const handleCancelPublic = () => {
    setShowConfirmDialog(false);
    setPendingValue(null);
  };

  const updateVisibility = (newValue) => {
    setLocalValue(newValue);

    // Dispatch Redux action
    dispatch(updateIntegrationVisibility(integrationId, newValue));

    // Call optional callback
    if (onUpdate) {
      onUpdate(newValue);
    }
  };

  return (
    <Box className="integration-toggle">
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <Select
          value={localValue}
          onChange={handleChange}
          disabled={disabled}
          sx={{
            height: '36px',
            fontSize: '13px',
            borderColor: 'var(--primary-color)',
            backgroundColor: 'var(--sa-dark-surface-light)',
            '& .MuiSelect-select': {
              py: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: localValue === 'public' ? 'success.main' : 'var(--primary-color)',
            }
          }}
          renderValue={(value) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {value === 'private' ? (
                <>
                  <PrivateIcon sx={{ fontSize: 16, color: 'var(--primary-color)' }} />
                  <Typography variant="body2" sx={{ fontSize: '13px' }}>Private</Typography>
                </>
              ) : (
                <>
                  <PublicIcon sx={{ fontSize: 16, color: 'success.main' }} />
                  <Typography variant="body2" sx={{ fontSize: '13px' }}>Public</Typography>
                </>
              )}
            </Box>
          )}
        >
          <MenuItem value="private">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
              <PrivateIcon sx={{ fontSize: 18, color: 'var(--primary-color)' }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px' }}>
                  Private
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary' }}>
                  Only you can use
                </Typography>
              </Box>
            </Box>
          </MenuItem>
          <MenuItem value="public">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
              <PublicIcon sx={{ fontSize: 18, color: 'success.main' }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px' }}>
                  Public
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary' }}>
                  Shared with team
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        </Select>
      </FormControl>

      {/* Confirmation Dialog for Public */}
      <Modal isOpen={showConfirmDialog} toggle={handleCancelPublic} className={`main-modal new-user-modal modal-dialog-centered`}>
        <ModalHeader toggle={handleCancelPublic} ><h3><PublicIcon /> Make Integration Public?</h3></ModalHeader>
        <ModalBody>
          <div className='add-content mt-5'>
            <p> Making <strong>{integrationName}</strong> public will allow all members of your workspace to:</p>
            <ul style={{ marginTop: '10px', marginBottom: '10px' }}>
              <li>See this integration in their integration list</li>
              <li>Use this integration to send emails/messages</li>
              <li>View usage history (but not modify settings)</li>
            </ul>
            <p><strong>Note:</strong> Only you can modify or delete this integration.
              Other team members can only use it to send communications.</p>

            <Alert color="info" className="mb-3">
              💡 <strong>Tip:</strong> Public integrations are useful for shared team
              accounts like <em>support@company.com</em> or company phone numbers.
            </Alert>
            <div className='templates-inner mt-4'>
              <div className='btn-group'>
                <button className='btn-style-border mr-3' onClick={handleCancelPublic} color="inherit">
                  Cancel
                </button>
                <button
                  onClick={handleConfirmPublic}
                  variant="contained"
                  color="success"
                  className='btn-style-new'
                >
                  <PublicIcon sx={{width: '15px', marginRight: '5px', marginTop: '-1px'}} /> Make Public
                </button>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </Box>
  );
};

export default IntegrationToggle;
